import { Head } from "vite-react-ssg";
import { Section, SectionHeading, Stat, CodeCard, Pill } from "@/components/kit";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Eyebrow } from "@/components/Eyebrow";
import { HeroBackdrop } from "@/components/HeroBackdrop";
import { AgentLogo } from "@/components/AgentLogo";
import aiHero from "@/assets/hero/ai-hero.jpg";
import {
  AI_HERO,
  BYOA_HEADING,
  BYOA_AGENTS,
  BYOA_PATHS,
  BYOA_NOTE,
  ASSISTANT_HEADING,
  CAPABILITY_CARDS,
  APPROVAL_NOTE,
  DJL_HEADING,
  DJL_FEATURES,
  DJL_CODE,
  MCP_HEADING,
  MCP_CODE,
  MCP_ENTRY_POINTS,
  OPERATOR_HEADING,
  OPERATOR_COMMANDS,
  OPERATOR_BLURB,
  AI_STATS,
  AI_CTA,
} from "@/content/ai";

export default function Ai() {
  return (
    <>
      <Head>
        <title>AI & Automation — DjiniousLab</title>
        <meta
          name="description"
          content="Drive DjiniousLab from external AI agents like Claude Code, OpenAI Codex, and Pi — via the integrated MCP server or the dedicated djl CLI client — plus an in-app assistant, all behind the same approval gates the UI uses."
        />
      </Head>

      {/* ── Hero ──────────────────────────────────────────────────────────── */}
      <HeroBackdrop image={aiHero}>
        <Eyebrow>{AI_HERO.eyebrow}</Eyebrow>
        <h1 className="mt-5 text-4xl font-extrabold leading-tight tracking-tight text-foreground sm:text-5xl">
          {AI_HERO.title}
        </h1>
        <p className="mt-5 text-xl leading-relaxed text-muted-foreground">
          {AI_HERO.intro}
        </p>
      </HeroBackdrop>

      {/* ── Stat strip ────────────────────────────────────────────────────── */}
      <Section bordered>
        <div className="grid grid-cols-2 gap-y-10 sm:grid-cols-4">
          {AI_STATS.map((s) => (
            <Stat key={s.label} value={s.value} label={s.label} />
          ))}
        </div>
      </Section>

      {/* ── Section 1 — In-app assistant ──────────────────────────────────── */}
      <Section wash="wash">
        <SectionHeading
          eyebrow={ASSISTANT_HEADING.eyebrow}
          title={ASSISTANT_HEADING.title}
          intro={ASSISTANT_HEADING.intro}
        />

        {/* Capability cards grid */}
        <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CAPABILITY_CARDS.map((card) => (
            <Card key={card.group} className="flex flex-col gap-3">
              <Eyebrow>{card.group}</Eyebrow>
              <h3 className="text-base font-bold text-foreground">{card.title}</h3>
              <ul className="flex flex-col gap-2">
                {card.items.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-sm leading-relaxed text-muted-foreground">
                    <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    {item}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        {/* Approval gate callout */}
        <div className="mt-10 rounded-2xl border border-border bg-surface px-8 py-6">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:gap-6">
            <div className="shrink-0">
              <span className="inline-flex h-9 w-9 items-center justify-center rounded-xl bg-primary-tint text-primary text-lg font-bold">
                ✓
              </span>
            </div>
            <div>
              <h3 className="text-sm font-bold text-foreground">{APPROVAL_NOTE.title}</h3>
              <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{APPROVAL_NOTE.body}</p>
            </div>
          </div>
        </div>
      </Section>

      {/* ── Section — Bring your own external agent ───────────────────────── */}
      <Section bordered>
        <SectionHeading
          eyebrow={BYOA_HEADING.eyebrow}
          title={BYOA_HEADING.title}
          intro={BYOA_HEADING.intro}
        />

        {/* Agent grid */}
        <div className="mx-auto mt-10 grid max-w-4xl grid-cols-2 gap-3 sm:grid-cols-3">
          {BYOA_AGENTS.map((a) => (
            <div
              key={a.name}
              className="flex flex-col items-center justify-center gap-1 rounded-xl border border-border bg-card px-4 py-4 text-center shadow-soft"
            >
              {a.logo ? (
                <AgentLogo name={a.logo} className="mb-1.5 h-7 w-7 text-foreground" />
              ) : null}
              <span className="text-sm font-bold text-foreground">{a.name}</span>
              <span className="font-mono text-[11px] text-muted-foreground">{a.via}</span>
            </div>
          ))}
        </div>

        {/* Two connection paths */}
        <div className="mt-10 grid gap-5 md:grid-cols-2">
          {BYOA_PATHS.map((path) => (
            <Card key={path.label} className="flex flex-col gap-4">
              <div className="flex items-center justify-between gap-3">
                <h3 className="text-lg font-bold text-foreground">{path.label}</h3>
                <Pill tone={path.tone}>{path.tagline}</Pill>
              </div>
              <ul className="flex flex-col gap-2.5">
                {path.points.map((pt) => (
                  <li key={pt} className="flex items-start gap-2.5 text-sm leading-relaxed text-muted-foreground">
                    <span
                      className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${path.tone === "primary" ? "bg-primary" : "bg-accent"}`}
                      aria-hidden="true"
                    />
                    {pt}
                  </li>
                ))}
              </ul>
            </Card>
          ))}
        </div>

        <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed text-muted-foreground">
          {BYOA_NOTE}
        </p>
      </Section>

      {/* ── Section 2 — djl CLI ───────────────────────────────────────────── */}
      <Section wash="surface">
        <div className="grid items-start gap-12 lg:grid-cols-2">
          {/* Left: heading + feature list */}
          <div className="flex flex-col gap-6">
            <SectionHeading
              align="left"
              eyebrow={DJL_HEADING.eyebrow}
              title={DJL_HEADING.title}
              intro={DJL_HEADING.intro}
            />
            <ul className="flex flex-col gap-3">
              {DJL_FEATURES.map((feat) => (
                <li key={feat} className="flex items-center gap-3 text-sm text-foreground">
                  <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-accent" aria-hidden="true" />
                  {feat}
                </li>
              ))}
            </ul>
          </div>
          {/* Right: code card */}
          <CodeCard title="djl" lang="shell" code={DJL_CODE} />
        </div>
      </Section>

      {/* ── Section 3 — MCP server ────────────────────────────────────────── */}
      <Section>
        <div className="grid items-start gap-12 lg:grid-cols-2">
          {/* Left: code card */}
          <CodeCard title="MCP token + agent config" lang="shell" code={MCP_CODE} />
          {/* Right: heading + entry-points list */}
          <div className="flex flex-col gap-6">
            <SectionHeading
              align="left"
              eyebrow={MCP_HEADING.eyebrow}
              title={MCP_HEADING.title}
              intro={MCP_HEADING.intro}
            />
            <div className="flex flex-col gap-4">
              {MCP_ENTRY_POINTS.map((ep) => (
                <div key={ep.label} className="rounded-xl border border-border bg-surface px-4 py-4">
                  <p className="text-sm font-bold text-foreground">{ep.label}</p>
                  <p className="mt-0.5 text-sm text-muted-foreground">{ep.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* ── Section 4 — operator CLI ──────────────────────────────────────── */}
      <Section wash="wash" bordered>
        <SectionHeading
          eyebrow={OPERATOR_HEADING.eyebrow}
          title={OPERATOR_HEADING.title}
          intro={OPERATOR_HEADING.intro}
        />
        <div className="mt-8 flex flex-wrap justify-center gap-3">
          {OPERATOR_COMMANDS.map((cmd) => (
            <Pill key={cmd.label} tone={cmd.tone}>
              {cmd.label}
            </Pill>
          ))}
        </div>
        <p className="mx-auto mt-8 max-w-2xl text-center text-sm leading-relaxed text-muted-foreground">
          {OPERATOR_BLURB}
        </p>
      </Section>

      {/* ── CTA band ──────────────────────────────────────────────────────── */}
      <Section wash="surface">
        <div className="flex flex-col items-center gap-4 text-center">
          <h2 className="mx-auto max-w-2xl text-3xl font-extrabold tracking-tight text-foreground sm:text-4xl">
            {AI_CTA.heading}
          </h2>
          <p className="mx-auto max-w-xl text-base leading-relaxed text-muted-foreground">
            {AI_CTA.body}
          </p>
          <div className="mt-2 flex flex-wrap justify-center gap-3">
            <Button to={AI_CTA.primary.to}>{AI_CTA.primary.label}</Button>
            <Button to={AI_CTA.secondary.to} variant="ghost">
              {AI_CTA.secondary.label}
            </Button>
          </div>
        </div>
      </Section>
    </>
  );
}
